import { useState, useEffect, useCallback, useRef } from 'react';
import { AlertTriangle, ArrowLeft, AlertCircle, Tag, Check, X } from 'lucide-react';
import { Loader } from './ui/Loader';
import { API_BASE } from '../utils/supabase/info';
import { CURRENT_TERMS_VERSION } from '../utils/terms-version';
import { devLog, devError } from '../utils/logger';
import { PageShell } from './layout/PageShell';

declare global {
  interface Window {
    Dibs?: any;
  }
}

const NEXI_CHECKOUT_JS_URL = import.meta.env?.VITE_NEXI_CHECKOUT_JS_URL ?? '';
const NEXI_CHECKOUT_KEY = import.meta.env?.VITE_NEXI_CHECKOUT_KEY ?? '';

interface StripeCheckoutProps {
  planType: 'talent_plus' | 'arbeidsgiver' | 'arbeidsgiver_plus' | null;
  // Kept for backwards compatibility with existing checkout-view wiring.
  // Nets pricing is amount-based; priceId is no longer used.
  priceId?: string;
  onBack: () => void;
  accessToken: string;
  userId: string;
  onViewTerms?: () => void;
  onViewWithdrawalForm?: () => void;
  onSuccess?: (planType: string) => void; // ✅ NEW: Callback when payment succeeds
  userType?: 'talent' | 'employer'; // ✅ NEW: User type to determine business/consumer
}

export function StripeCheckout({ planType, priceId, onBack, accessToken, userId, onViewTerms, onViewWithdrawalForm, onSuccess, userType }: StripeCheckoutProps) {
  const [paymentId, setPaymentId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isBusinessPurchase, setIsBusinessPurchase] = useState(userType === 'employer'); // ✅ Auto-set based on userType
  const [acknowledgedWithdrawal, setAcknowledgedWithdrawal] = useState(false);
  const [confirmedAge18, setConfirmedAge18] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const checkoutInstanceRef = useRef<any>(null);
  const checkoutRef = useRef<HTMLDivElement>(null);
  
  // ✅ DISCOUNT CODE STATE
  const [discountCode, setDiscountCode] = useState('');
  const [discountValidating, setDiscountValidating] = useState(false);
  const [discountError, setDiscountError] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<{
    code: string;
    discountType: 'percentage' | 'fixed';
    discountValue: number;
  } | null>(null);

  // Ordreoppsummering (pris og fornyelse) – forbrukerkjøpsloven: tydelig info før binding
  const [planSummary, setPlanSummary] = useState<{ planName: string; priceKr: number } | null>(null);

  // ✅ VALIDATE DISCOUNT CODE
  const validateDiscountCode = async () => {
    if (!discountCode.trim()) return;
    
    setDiscountValidating(true);
    setDiscountError('');
    
    try {
      const response = await fetch(
        `${API_BASE}/discount/validate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code: discountCode.trim().toUpperCase(), subscriptionType: planType }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.valid) {
        setDiscountError(data.error || 'Ugyldig rabattkode');
        setAppliedDiscount(null);
      } else {
        setAppliedDiscount({
          code: discountCode.trim().toUpperCase(),
          discountType: data.discount.discountType ?? data.discount.type,
          discountValue: data.discount.discountValue ?? data.discount.value,
        });
        setDiscountError('');
      }
    } catch (err: any) {
      devError('Error validating discount code:', err);
      setDiscountError('Kunne ikke validere rabattkode');
      setAppliedDiscount(null);
    } finally {
      setDiscountValidating(false);
    }
  };

  // ✅ REMOVE APPLIED DISCOUNT
  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
    setDiscountError('');
  };

  useEffect(() => {
    if (!planType) {
      setError('Ugyldig abonnementstype');
      setLoading(false);
      return;
    }
    setLoading(false);
  }, [planType]);

  // Hent planpris for ordreoppsummering (tydelig pris før kjøp)
  useEffect(() => {
    if (!planType) return;
    const planNames: Record<string, string> = {
      talent_plus: 'Talentfunn Pluss',
      arbeidsgiver: 'Arbeidsgiver Basis',
      arbeidsgiver_plus: 'Arbeidsgiver Pluss',
    };
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/nets/plans`, { headers: { Authorization: `Bearer ${accessToken}` } });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const key = planType === 'talent_plus' ? 'talentPlusMonthly' : planType === 'arbeidsgiver' ? 'arbeidsgiverMonthly' : 'arbeidsgiverPlusMonthly';
        const plan = data?.plans?.[key];
        const amountOre = plan?.amountOre ?? 0;
        if (cancelled) return;
        setPlanSummary({
          planName: planNames[planType] || planType,
          priceKr: Math.round(amountOre / 100),
        });
      } catch {
        if (!cancelled) setPlanSummary({ planName: planNames[planType] || planType, priceKr: 0 });
      }
    })();
    return () => { cancelled = true; };
  }, [planType, accessToken]);

  const loadCheckoutJs = useCallback(async () => {
    if (typeof window === 'undefined') return;
    if (window.Dibs?.Checkout) return;
    if (!NEXI_CHECKOUT_JS_URL) throw new Error('Mangler VITE_NEXI_CHECKOUT_JS_URL');

    const existing = document.querySelector('script[data-nexi-checkout-js="true"]') as HTMLScriptElement | null;
    if (existing) {
      await new Promise<void>((resolve, reject) => {
        if (window.Dibs?.Checkout) return resolve();
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('Kunne ikke laste checkout.js')), { once: true });
      });
      return;
    }

    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = NEXI_CHECKOUT_JS_URL;
      script.async = true;
      script.dataset.nexiCheckoutJs = 'true';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Kunne ikke laste checkout.js'));
      document.head.appendChild(script);
    });
  }, []);

  // ✅ CREATE PAYMENT (called when user clicks "Fortsett til betaling")
  const createCheckoutSession = async () => {
    try {
      setLoading(true);
      setError('');

      if (!planType) throw new Error('Ugyldig abonnementstype');
      if (!NEXI_CHECKOUT_KEY) throw new Error('Mangler VITE_NEXI_CHECKOUT_KEY');

      await loadCheckoutJs();

      const isUpdatePaymentDetailsFlow = priceId === '__UPDATE_PAYMENT_DETAILS__';

      const requestBody: any = {
        planType,
        plan_type: planType,
        userId,
        termsAcceptedVersion: CURRENT_TERMS_VERSION,
        checkoutUrl: typeof window !== 'undefined' ? window.location.origin : '',
        checkout_url: typeof window !== 'undefined' ? window.location.origin : '',
      };

      // ✅ ADD DISCOUNT CODE IF APPLIED
      if (!isUpdatePaymentDetailsFlow && appliedDiscount) {
        requestBody.discountCode = appliedDiscount.code;
        requestBody.discount_code = appliedDiscount.code;
      }

      let response: Response;
      if (isUpdatePaymentDetailsFlow) {
        // Fetch the existing subscriptionId and create a payment that updates card details.
        const statusRes = await fetch(`${API_BASE}/nets/subscription-status`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const statusData = await statusRes.json().catch(() => null);
        const subscriptionId = statusData?.subscription?.subscriptionId;
        if (!statusRes.ok || !subscriptionId) {
          throw new Error(statusData?.error || 'Fant ikke abonnementet ditt');
        }

        response = await fetch(`${API_BASE}/nets/create-payment-update-subscription`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            subscriptionId,
            subscription_id: subscriptionId,
            checkoutUrl: typeof window !== 'undefined' ? window.location.origin : '',
            checkout_url: typeof window !== 'undefined' ? window.location.origin : '',
          }),
        });
      } else {
        response = await fetch(
          `${API_BASE}/nets/create-payment`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
          }
        );
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Kunne ikke opprette betalingsøkt');
      }

      const data = await response.json();
      setPaymentId(data.paymentId);
      setShowCheckout(true);
    } catch (err: any) {
      devError('Error creating checkout session:', err);
      setError(err.message || 'Noe gikk galt. Vennligst prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentCompleted = useCallback(async (completedPaymentId: string) => {
    try {
      devLog('✅ Payment completed (Nexi). Verifying...', completedPaymentId);
      const response = await fetch(`${API_BASE}/nets/verify-payment`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentId: completedPaymentId,
          payment_id: completedPaymentId,
          discountCode: appliedDiscount?.code || null,
          discount_code: appliedDiscount?.code || null,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || 'Kunne ikke verifisere betaling');
      }

      if (onSuccess) onSuccess(planType || 'talent_plus');
    } catch (e: any) {
      devError('❌ Error verifying payment:', e);
      setError(e?.message || 'Kunne ikke verifisere betaling');
    }
  }, [accessToken, appliedDiscount?.code, onSuccess, planType]);

  const handlePaymentCompletedRef = useRef(handlePaymentCompleted);
  handlePaymentCompletedRef.current = handlePaymentCompleted;

  // Initialize the embedded checkout iframe once paymentId is available.
  // Effect deps exclude handlePaymentCompleted so we don't re-init on every parent re-render (avoids flicker + mass SVG rect errors).
  useEffect(() => {
    if (!showCheckout) return;
    if (!paymentId) return;
    if (typeof window === 'undefined') return;

    let cancelled = false;

    (async () => {
      try {
        await loadCheckoutJs();
        if (cancelled) return;
        const CheckoutCtor = window.Dibs?.Checkout;
        if (!CheckoutCtor) throw new Error('checkout.js ikke tilgjengelig');
        if (!NEXI_CHECKOUT_KEY) throw new Error('Mangler VITE_NEXI_CHECKOUT_KEY');

        const container = document.getElementById('checkout-container-div');
        if (!container) throw new Error('Checkout-container mangler');

        // Wait for container to have width before Nexi init (avoids rect width -60 errors when container is 0-width).
        const deadline = Date.now() + 2000;
        while (container.getBoundingClientRect().width <= 0 && Date.now() < deadline) {
          await new Promise((r) => requestAnimationFrame(r));
          if (cancelled) return;
        }

        try {
          checkoutInstanceRef.current?.cleanup?.();
        } catch {}
        checkoutInstanceRef.current = null;
        container.innerHTML = '';

        const checkout = new CheckoutCtor({
          checkoutKey: NEXI_CHECKOUT_KEY,
          paymentId,
          containerId: 'checkout-container-div',
          language: 'nb-NO',
        });

        checkout.on('payment-completed', (response: any) => {
          const completedPaymentId = response?.paymentId || paymentId;
          handlePaymentCompletedRef.current(completedPaymentId);
        });

        checkoutInstanceRef.current = checkout;
      } catch (e: any) {
        devError('Error initializing embedded checkout:', e);
        setError(e?.message || 'Kunne ikke starte betaling');
      }
    })();

    return () => {
      cancelled = true;
      try {
        checkoutInstanceRef.current?.cleanup?.();
      } catch {}
      checkoutInstanceRef.current = null;
      const container = document.getElementById('checkout-container-div');
      if (container) container.innerHTML = '';
    };
  }, [showCheckout, paymentId, loadCheckoutJs]);

  // When Stripe checkout appears (mobil: "Fortsett til betaling"), scroll so Stripe-betalingsmodulen er øverst
  useEffect(() => {
    if (!showCheckout) return;
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 1280;
    const t = setTimeout(() => {
      if (isMobile && checkoutRef.current) {
        checkoutRef.current.scrollIntoView({ block: 'start', behavior: 'auto' });
      } else {
        const container = document.querySelector('.animated-pages-container-scroll') as HTMLElement | null;
        if (container) container.scrollTo({ top: 0, behavior: 'auto' });
        else window.scrollTo({ top: 0, behavior: 'auto' });
      }
    }, 150);
    return () => clearTimeout(t);
  }, [showCheckout]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <PageShell className="flex-1 flex items-center justify-center px-4 pt-page-safe-sm">
          <div className="text-center">
            <Loader size="2xl" className="text-purple-600 mx-auto mb-4" />
            <p className="text-gray-600">Laster betalingsskjema...</p>
          </div>
        </PageShell>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <PageShell className="flex-1 flex items-center justify-center px-4 pt-page-safe-sm">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl mb-4 text-gray-800">Noe gikk galt</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={onBack}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all"
            >
              <ArrowLeft className="w-5 h-5 inline mr-2" />
              Gå tilbake
            </button>
          </div>
        </PageShell>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PageShell className="pb-bottom-nav-safe pt-page-safe-sm xl:max-w-[1280px] 2xl:max-w-[1400px] xl:mx-auto xl:px-8 xl:pt-5 xl:pb-24">
        <div className="pt-5 px-4 xl:px-0 max-w-4xl xl:max-w-[1280px] 2xl:max-w-[1400px] mx-auto">
          {/* Header – tilbakeknapp og topp-padding som på FAQ */}
          <div className="mb-8">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-purple-600 hover:text-purple-700 mb-6 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Tilbake</span>
            </button>
            <h1 className="text-3xl sm:text-4xl mb-4 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1">
            {planType === 'arbeidsgiver_plus' 
              ? 'Arbeidsgiver Pluss'
              : planType === 'arbeidsgiver' 
                ? 'Arbeidsgiver Basis' 
                : 'Talentfunn Pluss'}
          </h1>
          <p className="text-gray-600 mb-8">
            {planType === 'arbeidsgiver_plus'
              ? 'Meldinger, favoritter og lagrede søk med varsling'
              : planType === 'arbeidsgiver'
                ? 'Få tilgang til å kontakte alle talenter'
                : 'Ubegrensede portfolioer, bilder og video'}
          </p>
        </div>

        {!showCheckout ? (
          <div className="mb-8">
            {/* Ordreoppsummering – tydelig pris og fornyelse før binding (forbrukerkjøpsloven) */}
            {planSummary && (
              <div className="mb-8 p-6 bg-gray-50 border-2 border-gray-200 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Ordreoppsummering</h3>
                <p className="text-gray-800">
                  <strong>{planSummary.planName}</strong>
                  {planSummary.priceKr > 0 && (
                    <span className="text-gray-700"> — {planSummary.priceKr} kr/mnd (eks. mva)</span>
                  )}
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  Abonnementet fornyes automatisk hver måned til du avbryter. Du kan avslutte når som helst under Innstillinger → Abonnement.
                </p>
              </div>
            )}

            {/* Consumer Information (for talent/consumers only) */}
            {!isBusinessPurchase && (
              <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border-2 border-blue-200">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg text-blue-900 font-medium mb-2">Digital tjeneste</h3>
                    <p className="text-sm text-blue-800">
                      Talentfunn Pluss leveres umiddelbart etter betaling. I henhold til Angrerettloven § 22 bokstav n bortfaller angreretten når du samtykker til umiddelbar levering. Du kan likevel si opp abonnementet når som helst via Innstillinger.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Business Purchase Notice (for employers only) */}
            {isBusinessPurchase && (
              <div className="mb-8 p-6 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl border-2 border-orange-200">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg text-orange-900 font-medium mb-2">Næringskjøp</h3>
                    <p className="text-sm text-orange-800">
                      Som verifisert bedrift har du <strong>ikke lovfestet angrefrist</strong>. Avtalen er bindende fra kjøpstidspunktet. 
                      Du kan likevel kansellere abonnementet når som helst, men du får ikke refusjon for inneværende periode.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Age requirement for subscription purchases (for talent/consumers only) */}
            {!isBusinessPurchase && (
              <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg text-blue-900 font-medium mb-2">Alderskrav</h3>
                    <p className="text-sm text-blue-800">
                      For å inngå avtale om Pluss-abonnement må du være over 18 år.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Terms Acceptance */}
            <div className="space-y-4 mb-6">
              {/* Accept Terms */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 w-5 h-5 text-purple-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
                />
                <span className="text-gray-700">
                  Jeg har lest og aksepterer {' '}
                  {onViewTerms ? (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        onViewTerms();
                      }}
                      className="text-purple-600 hover:text-purple-700 underline font-medium"
                    >
                      vilkår og betingelser
                    </button>
                  ) : (
                    <span className="text-purple-600 font-medium">vilkår og betingelser</span>
                  )}
                </span>
              </label>

              {/* Confirm 18+ (for consumers) */}
              {!isBusinessPurchase && (
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={confirmedAge18}
                    onChange={(e) => setConfirmedAge18(e.target.checked)}
                    className="mt-1 w-5 h-5 text-purple-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
                  />
                  <span className="text-gray-700">
                    Jeg bekrefter at jeg er over 18 år
                  </span>
                </label>
              )}

              {/* Acknowledge service starts immediately – angrerett goes tapt (for consumers) */}
              {!isBusinessPurchase && (
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acknowledgedWithdrawal}
                    onChange={(e) => setAcknowledgedWithdrawal(e.target.checked)}
                    className="mt-1 w-5 h-5 text-purple-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
                  />
                  <span className="text-gray-700">
                    Jeg samtykker til at tjenesten starter umiddelbart og at angreretten dermed går tapt
                  </span>
                </label>
              )}
            </div>

            {/* ✅ DISCOUNT CODE SECTION */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Tag className="w-5 h-5 text-gray-600" />
                <h3 className="text-gray-800 font-medium">Har du en rabattkode?</h3>
              </div>
              
              {!appliedDiscount ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        validateDiscountCode();
                      }
                    }}
                    placeholder="Skriv inn kode"
                    className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 uppercase"
                    disabled={discountValidating}
                  />
                  <button
                    onClick={validateDiscountCode}
                    disabled={!discountCode.trim() || discountValidating}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {discountValidating ? (
                      <>
                        <Loader size="sm" />
                        <span>Validerer...</span>
                      </>
                    ) : (
                      <span>Bruk kode</span>
                    )}
                  </button>
                </div>
              ) : (
                <div className="p-4 bg-green-50 border-2 border-green-500 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                      <Check className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="font-medium text-green-900">
                        Kode brukt: {appliedDiscount.code}
                      </div>
                      <div className="text-sm text-green-700">
                        {appliedDiscount.discountType === 'percentage'
                          ? `${appliedDiscount.discountValue}% rabatt`
                          : `${appliedDiscount.discountValue} kr rabatt`}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={removeDiscount}
                    className="p-2 hover:bg-green-100 rounded-lg transition-colors"
                    title="Fjern rabattkode"
                  >
                    <X className="w-5 h-5 text-green-700" />
                  </button>
                </div>
              )}

              {discountError && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-700">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  <span>{discountError}</span>
                </div>
              )}
            </div>

            {/* Continue Button */}
            <button
              onClick={() => {
                if (!isBusinessPurchase && (!acknowledgedWithdrawal || !confirmedAge18)) {
                  return;
                }
                createCheckoutSession();
              }}
              disabled={!acceptedTerms || (!isBusinessPurchase && (!acknowledgedWithdrawal || !confirmedAge18))}
              className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium text-lg"
            >
              Fortsett til betaling
            </button>

            <p className="text-center text-sm text-gray-500 mt-4">
              Sikker betaling via Nets
            </p>
            {/* Nets compliance: virksomhetsinfo og kortlogoer tydelig under kjøp */}
            <div className="mt-4 pt-4 border-t border-gray-200 text-center space-y-2">
              <p className="text-xs text-gray-600">
                Innovara AS · Org.nr: 933 184 048 · Norge
              </p>
              <div className="flex justify-center gap-4 items-center">
                <a href="https://www.visa.no" target="_blank" rel="noopener noreferrer" className="opacity-80 hover:opacity-100" aria-label="Visa">
                  <img src="https://cdn.jsdelivr.net/npm/credit-card-logos@1.0.5/img/card-logo-visa.svg" alt="Visa" className="h-6" />
                </a>
                <a href="https://www.mastercard.no" target="_blank" rel="noopener noreferrer" className="opacity-80 hover:opacity-100" aria-label="Mastercard">
                  <img src="https://cdn.jsdelivr.net/npm/credit-card-logos@1.0.5/img/card-logo-mastercard.svg" alt="Mastercard" className="h-6" />
                </a>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Summary before checkout – innhold rett på siden, uten boks */}
            <div className="mb-6">
              <h3 className="text-lg text-gray-800 mb-3">Oppsummering</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Kjøper som:</span>
                  <span className="text-gray-800 font-medium">
                    {isBusinessPurchase ? 'Bedrift (Næringsdrivende)' : 'Privatperson (Forbruker)'}
                  </span>
                </div>
                {!isBusinessPurchase && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Angrerett:</span>
                    <span className="text-gray-700">Bortfaller ved umiddelbar levering (jf. vilkår 5.7)</span>
                  </div>
                )}
              </div>
            </div>

            {/* Nets/Nexi Embedded Checkout iframe container – bred format på desktop for to-kolonne layout */}
            <div ref={checkoutRef} className="min-h-[60vh] w-full xl:min-w-[1100px]">
              <div id="checkout-container-div" className="min-h-[60vh] w-full [&_iframe]:xl:min-w-[1100px] [&_iframe]:xl:w-full" />
            </div>
            {/* Nets compliance: virksomhetsinfo og kortlogoer tydelig under betaling */}
            <div className="mt-6 pt-4 border-t border-gray-200 text-center space-y-2">
              <p className="text-xs text-gray-600">
                Innovara AS · Org.nr: 933 184 048 · Norge
              </p>
              <div className="flex justify-center gap-4 items-center">
                <a href="https://www.visa.no" target="_blank" rel="noopener noreferrer" className="opacity-80 hover:opacity-100" aria-label="Visa">
                  <img src="https://cdn.jsdelivr.net/npm/credit-card-logos@1.0.5/img/card-logo-visa.svg" alt="Visa" className="h-6" />
                </a>
                <a href="https://www.mastercard.no" target="_blank" rel="noopener noreferrer" className="opacity-80 hover:opacity-100" aria-label="Mastercard">
                  <img src="https://cdn.jsdelivr.net/npm/credit-card-logos@1.0.5/img/card-logo-mastercard.svg" alt="Mastercard" className="h-6" />
                </a>
              </div>
            </div>
          </>
        )}
        </div>
      </PageShell>
    </div>
  );
}
